import type { IconName } from "@sagot/ui";

/** Tickets list (C01, SD-S1-43): sizes, timings and vocabularies of the screen. */

/** Server page size (the API allows up to 200; 50 keeps the first paint fast and matches UX-010). */
export const TICKET_PAGE_SIZE = 50;
export const TICKET_SEARCH_DEBOUNCE_MS = 300;
/** Milliseconds a view count stays fresh (UX-002 caches counts for at most 30 s). */
export const TICKET_COUNT_STALE_MS = 30_000;

/** Fixed heights (px) the virtual window multiplies by; the Tailwind classes that set them are `h-20` and `h-44`. */
export const TICKET_ROW_HEIGHT = 80;
export const TICKET_CARD_HEIGHT = 176;
export const TICKET_HEADER_HEIGHT = 40;
/** Rows drawn above and below the visible ones so a fast scroll never shows a gap. */
export const TICKET_OVERSCAN = 6;
/** Viewport height assumed until the scroller is measured. */
export const TICKET_DEFAULT_VIEWPORT = 640;
/** The next page is requested when the window comes this close to the last loaded row. */
export const TICKET_PREFETCH_ROWS = 15;

/** The system views the server lists first (`GET /v1/ticket-views`), in rail order. Their words live in `service.tickets.views.<id>`. */
export const SYSTEM_VIEW_IDS = ["unassigned", "my_open", "my_team", "waiting_client", "breaching_soon", "all_open", "resolved", "closed"] as const;
export type SystemViewId = (typeof SYSTEM_VIEW_IDS)[number];
export const DEFAULT_VIEW: SystemViewId = "all_open";

export const VIEW_ICONS: Readonly<Record<SystemViewId, IconName>> = {
  unassigned: "inbox",
  my_open: "user",
  my_team: "users",
  waiting_client: "hourglass",
  breaching_soon: "alert-triangle",
  all_open: "folder-open",
  resolved: "check-circle",
  closed: "archive",
};

export const FILTER_KEYS = ["status", "priority", "type", "team", "assignee", "organisation", "tag"] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];

/** Search parameters of the list URL, so a view, a filter set or a search can be bookmarked and survives Back from a ticket. */
export const TICKET_PARAM = {
  view: "view",
  q: "q",
  sort: "sort",
  status: "status",
  priority: "priority",
  type: "type",
  team: "team",
  assignee: "assignee",
  organisation: "org",
  tag: "tag",
} as const satisfies Record<string, string>;

/** `assignee` values the API understands besides a user id. */
export const ASSIGNEE_ME = "me";
export const ASSIGNEE_NONE = "none";

/** What the server sorts by (`updated_at`, `created_at`, `priority`, `sla_due`); the other columns do not sort. */
export const SORT_FIELD = { updated: "updated_at", created: "created_at", priority: "priority", sla: "sla_due" } as const;
export type SortKey = keyof typeof SORT_FIELD;
export type SortDirection = "asc" | "desc";
export interface TicketSort {
  key: SortKey;
  dir: SortDirection;
}
export const DEFAULT_SORT: TicketSort = { key: "updated", dir: "desc" };

export const COLUMN_KEYS = ["number", "title", "org", "requester", "type", "status", "priority", "assignee", "sla", "updated"] as const;
export type ColumnKey = (typeof COLUMN_KEYS)[number];
/** The columns whose header sorts the list, and what each sorts by. */
export const SORTABLE_COLUMNS: Readonly<Partial<Record<ColumnKey, SortKey>>> = { priority: "priority", sla: "sla", updated: "updated" };
/** The name the server gives a column in a saved view (`ViewColumn`). */
export const SERVER_COLUMN = {
  number: "number",
  title: "title",
  org: "organisation",
  requester: "requester",
  type: "type",
  status: "status",
  priority: "priority",
  assignee: "assignee",
  sla: "sla",
  updated: "updated",
} as const satisfies Record<ColumnKey, string>;
/** Always shown: the ticket's identity. */
export const FIXED_COLUMNS: readonly ColumnKey[] = ["number", "title"];
export const DEFAULT_COLUMNS: readonly ColumnKey[] = ["number", "title", "status", "priority", "assignee", "sla"];
/** From this viewport width the table also has room for the organisation, the requester and the update time (mockup). */
export const WIDE_COLUMNS: readonly ColumnKey[] = ["number", "title", "org", "requester", "status", "priority", "assignee", "sla", "updated"];
export const WIDE_COLUMNS_FROM_PX = 1536;
export const COLUMNS_STORAGE_KEY = "sagot.service.tickets.columns";

/** Deep links the ticket workspace (C02) reads on open; the list's shortcuts send people there (UX-003). */
export const TICKET_DETAIL_INTENT = {
  reply: { tab: "reply" },
  note: { tab: "note" },
  assign: { modal: "assign" },
  macro: { tab: "reply", macro: "1" },
  status: { status: "1" },
  merge: { modal: "merge" },
} as const satisfies Record<string, Readonly<Record<string, string>>>;
export type TicketDetailIntent = keyof typeof TICKET_DETAIL_INTENT;

/** New ticket (on behalf of a client). */
export const NEW_TICKET_TITLE_MIN = 5;
export const NEW_TICKET_TITLE_MAX = 120;
export const NEW_TICKET_DESCRIPTION_MAX = 2000;
export const CONTACT_SEARCH_LIMIT = 8;
export const CONTACT_SEARCH_MIN_CHARS = 1;
/** The only categories staff may raise a ticket for on behalf of a client. */
export const NEW_TICKET_TYPE_CATEGORY = "customer";
export const AUTO_ROUTE = "";
export const UNSUPPORTED_FIELD_TYPES = ["site", "order", "contact", "files", "serial"] as const;

/** Saved views: a name of this length, shown in the rail. */
export const SAVED_VIEW_NAME_MIN = 3;
export const SAVED_VIEW_NAME_MAX = 40;

/** Search: matching organisations and requesters by name takes a lookup, started from this many characters. */
export const SEARCH_LOOKUP_MIN_CHARS = 2;
export const SEARCH_LOOKUP_LIMIT = 20;
/** The filter language allows this many OR-groups; a search that would exceed it drops the organisation and requester matches. */
export const VIEW_GROUPS_MAX = 10;

/** The table replaces the card list from this viewport width (Tailwind `lg`: the six default columns do not fit a 768 px tablet, which gets the cards). */
export const TICKET_TABLE_FROM_PX = 1024;
